import React from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Row, Spin, Popconfirm, Empty } from 'antd';
import pluralize from 'pluralize';
import { Field } from 'redux-form';
import { SelectField, NumberField } from 'redux-form-antd';
import get from 'lodash/get';

import FormValueSelector from '../../../containers/FormValueSelector';
import ProductListModal from '../../../containers/ProductListModal';
import ProductFormModal from '../../../containers/ProductFormModal';
import CreativeListModal from '../../../containers/CreativeListModal';
import ImageLoader from '../../atoms/ImageLoader';

const formItemLayout = {
  colon: false,
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 16 },
  },
};

const pacingTypes = [
  { label: 'Daily', value: 'daily' },
  { label: 'Total', value: 'total' },
  { label: 'Unlimited', value: 'unlimited' },
];

const Products = ({
  productList,
  creativeList,
  showCreativeModal,
  showProductModal,
  removeProduct,
  handleSubmit,
  submitting,
}) => (
  <div className="p20">
    <h2>{pluralize('Product', productList.length, true)}</h2>
    <form style={{ padding: 20, backgroundColor: '#E7E7E7' }}>
      {productList.map(product => (
        <Row key={product.product_id} type="flex" align="middle" className="grayRow">
          <Col span={5}>
            <ImageLoader style={{ width: 'auto', margin: '0 auto' }} src={product.url_image} />
            <p style={{ textAlign: 'center' }}>
              {get(product, 'scan_taken') ? get(product, 'scan_taken').toLocaleString() : 'No'}{' '}
              Scans
            </p>
          </Col>
          <Col span={18}>
            <Row>
              <Col span={21} offset={3}>
                <p style={{ marginLeft: 8 }}>
                  <strong>{product.name}</strong>
                  <span className="right">
                    <Button
                      type="primary"
                      className="buttonLink"
                      onClick={() => showProductModal(product)}
                      ghost
                    >
                      Edit Product
                    </Button>
                    |{' '}
                    <Popconfirm
                      placement="topRight"
                      title="Are you sure you want to remove this product?"
                      onConfirm={() => {
                        removeProduct(product.product_id);
                      }}
                      okText="Yes"
                      cancelText="No"
                    >
                      <a className="removeProduct" href="#remove">
                        Remove
                      </a>
                    </Popconfirm>
                  </span>
                </p>
              </Col>
            </Row>

            <Row type="flex" justify="space-between" align="bottom">
              <Col span={10}>
                <Field
                  {...formItemLayout}
                  label="Points"
                  type="text"
                  parse={a => a && !isNaN(Number(a)) && Number(a)}
                  disabled
                  name={`product[${product.product_id}][pt_value]`}
                  component={NumberField}
                  min={0}
                />
                <Field
                  {...formItemLayout}
                  label="Pace"
                  name={`product[${product.product_id}][rate_type]`}
                  component={SelectField}
                  options={pacingTypes}
                />
                <FormValueSelector fieldName={`product[${product.product_id}][rate_type]`}>
                  {rate_type =>
                    rate_type !== 'unlimited' && (
                      <Field
                        {...formItemLayout}
                        label="Scans"
                        type="text"
                        parse={a => a && !isNaN(Number(a)) && Number(a)}
                        name={`product[${product.product_id}][budget]`}
                        component={NumberField}
                        min={0}
                      />
                    )
                  }
                </FormValueSelector>
              </Col>
              <Col span={14} style={{ textAlign: 'right' }}>
                <p style={{ marginBottom: 24 }}>
                  <strong>
                    {pluralize('Creative', get(creativeList, product.product_id, []).length, true)}
                  </strong>
                  <br />
                  <Button onClick={() => showCreativeModal(product)} style={{ marginTop: 5 }}>
                    {pluralize('Edit Creative', get(creativeList, product.product_id, []).length)}
                  </Button>
                </p>
              </Col>
            </Row>
          </Col>
        </Row>
      ))}

      <Row
        type="flex"
        align="middle"
        justify="space-between"
        className="grayRow"
        style={{ backgroundColor: 'transparent' }}
      >
        <Col span={24}>
          <div style={{ textAlign: 'right' }}>
            <Button type="primary" size="large" loading={submitting} onClick={handleSubmit}>
              Update
            </Button>
          </div>
        </Col>
      </Row>
    </form>
  </div>
);

const InHandTab = ({
  loading,
  removeProduct,
  showProductListModal,
  creativeList,
  productList,
  showProductModal,
  showCreativeModal,
  handleSubmit,
  submitting,
}) => (
  <div>
    <div>
      <Spin spinning={loading}>
        {!loading && !productList.length ? (
          <Empty description="No inHand Products">
            <Button type="primary" onClick={showProductModal}>
              Create Product
            </Button>
            <Button type="link" onClick={showProductListModal}>
              Add Existing Product
            </Button>
          </Empty>
        ) : null}

        {productList.length
          ? [
              <Button key={1} type="primary" onClick={showProductModal}>
                Create Product
              </Button>,
              <Button key={2} type="link" onClick={showProductListModal}>
                Add Existing Product
              </Button>,
              <Products
                key={3}
                productList={productList}
                creativeList={creativeList}
                showCreativeModal={showCreativeModal}
                showProductModal={showProductModal}
                removeProduct={removeProduct}
                handleSubmit={handleSubmit}
                submitting={submitting}
              />,
            ]
          : null}
      </Spin>
    </div>
    <ProductListModal />
    <CreativeListModal />
    <ProductFormModal />
  </div>
);

InHandTab.defaultProps = {
  submitting: false,
};

InHandTab.propTypes = {
  submitting: PropTypes.bool,
  handleSubmit: PropTypes.func.isRequired,
  removeProduct: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
  showProductModal: PropTypes.func.isRequired,
  showCreativeModal: PropTypes.func.isRequired,
};

export default InHandTab;
